/**
 * Page /admin/fournisseurs — CRUD du catalogue.
 *
 * Backend : GET/POST/PUT/DELETE /api/admin/suppliers (+ POST .../logo)
 * UI : grid de cards + modal d'édition avec tous les champs métier.
 */
import { useEffect, useRef, useState } from "react";
import api, { assetUrl } from "@/lib/api";
import { toast } from "sonner";
import { useConfirm } from "@/components/admin/ConfirmProvider";
import {
    Plus, Pencil, Trash2, X, Save, Loader2, Image as ImageIcon,
    Building2, Star, Upload, ExternalLink,
} from "lucide-react";

function _extractError(e) {
    const detail = e?.response?.data?.detail;
    if (typeof detail === "string") return detail;
    if (Array.isArray(detail)) return detail.map((d) => d?.msg || JSON.stringify(d)).join(" · ");
    if (detail && typeof detail === "object") return detail.msg || JSON.stringify(detail);
    return e?.message || "Erreur inconnue";
}

const EMPTY = {
    slug: "", name: "", tagline: "", description: "",
    logo: null, website: "", year_founded: null, headquarters: "",
    target_audience: "both",
    electricity: true, gas: false, green_energy: false,
    services: [], pros: [], cons: [],
    transparency_rating: 3, customer_service_rating: 3, green_rating: 3,
    price_rating: 3, digital_rating: 3, overall_rating: 3,
    price_tier: 3, best_for: "",
};


export default function AdminSuppliers() {
    const confirm = useConfirm();
    const [list, setList] = useState([]);
    const [loading, setLoading] = useState(true);
    const [editing, setEditing] = useState(null); // null | "new" | supplier doc
    const [search, setSearch] = useState("");

    async function load() {
        setLoading(true);
        try {
            const { data } = await api.get("/admin/suppliers");
            setList(data || []);
        } catch (e) {
            toast.error("Lecture : " + _extractError(e));
        } finally {
            setLoading(false);
        }
    }

    useEffect(() => { load(); }, []);

    async function remove(s) {
        if (!(await confirm({ title: "Supprimer ce fournisseur ?", message: `« ${s.name} » sera retiré du catalogue.`, confirmLabel: "Supprimer" }))) return;
        try {
            await api.delete(`/admin/suppliers/${s.slug}`);
            toast.success("Supprimé");
            load();
        } catch (e) {
            toast.error("Suppression : " + _extractError(e));
        }
    }

    const filtered = search
        ? list.filter((s) =>
            (s.name || "").toLowerCase().includes(search.toLowerCase()) ||
            (s.slug || "").includes(search.toLowerCase()))
        : list;

    return (
        <div className="em-container py-8">
            <div className="flex items-end justify-between flex-wrap gap-4">
                <div>
                    <p className="em-eyebrow">Catalogue</p>
                    <h1 className="font-display text-2xl md:text-3xl font-bold tracking-tight text-primary mt-2">
                        Fournisseurs
                    </h1>
                    <p className="text-muted-foreground mt-3">
                        Gestion du catalogue Energy Market — {list.length} fournisseurs.
                    </p>
                </div>
                <div className="flex items-center gap-2">
                    <input
                        type="text"
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        placeholder="Rechercher…"
                        className="rounded-full border border-border bg-card px-4 py-2 text-sm w-64"
                    />
                    <button
                        onClick={() => setEditing("new")}
                        className="inline-flex items-center gap-2 rounded-full px-5 py-2 text-sm font-semibold bg-primary text-primary-foreground hover:opacity-90"
                    >
                        <Plus className="h-4 w-4" /> Nouveau fournisseur
                    </button>
                </div>
            </div>

            {loading ? (
                <div className="mt-12 flex items-center justify-center text-muted-foreground">
                    <Loader2 className="h-5 w-5 animate-spin mr-2" /> Chargement…
                </div>
            ) : filtered.length === 0 ? (
                <div className="mt-12 rounded-3xl border border-border bg-card p-12 text-center">
                    <Building2 className="h-12 w-12 text-muted-foreground mx-auto mb-3" />
                    <p className="text-muted-foreground">Aucun fournisseur {search ? "ne correspond à la recherche" : "dans le catalogue"}.</p>
                    {!search && (
                        <button
                            onClick={() => setEditing("new")}
                            className="mt-4 inline-flex items-center gap-2 rounded-full px-5 py-2 text-sm font-semibold bg-primary text-primary-foreground hover:opacity-90"
                        >
                            <Plus className="h-4 w-4" /> Créer le premier
                        </button>
                    )}
                </div>
            ) : (
                <ul className="mt-8 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                    {filtered.map((s) => (
                        <li key={s.slug} className="rounded-3xl border border-border bg-card p-5 hover:border-primary/40 transition-colors">
                            <div className="flex items-start gap-3">
                                <div className="h-12 w-12 rounded-xl bg-muted overflow-hidden flex items-center justify-center shrink-0">
                                    {s.logo ? (
                                        <img src={assetUrl(s.logo)} alt={s.name} className="h-full w-full object-contain" />
                                    ) : (
                                        <ImageIcon className="h-5 w-5 text-muted-foreground" />
                                    )}
                                </div>
                                <div className="min-w-0 flex-1">
                                    <p className="font-display text-lg font-bold text-foreground truncate">{s.name}</p>
                                    <p className="text-xs text-muted-foreground truncate">{s.slug}</p>
                                </div>
                                <span className="inline-flex items-center gap-1 rounded-full bg-accent/20 text-accent-foreground px-2.5 py-0.5 text-xs font-semibold shrink-0">
                                    <Star className="h-3 w-3 fill-accent-foreground" /> {s.overall_rating?.toFixed(1) || "—"}
                                </span>
                            </div>
                            {s.tagline && <p className="text-sm text-muted-foreground mt-3 line-clamp-2">{s.tagline}</p>}
                            <div className="flex flex-wrap gap-1 mt-3">
                                {s.electricity && <Tag>Élec</Tag>}
                                {s.gas && <Tag>Gaz</Tag>}
                                {s.green_energy && <Tag color="green">Vert</Tag>}
                                <Tag color="neutral">{({b2c: "B2C", b2b: "B2B", both: "B2C+B2B"})[s.target_audience]}</Tag>
                            </div>
                            <div className="flex items-center gap-2 mt-4 pt-4 border-t border-border">
                                <button
                                    onClick={() => setEditing(s)}
                                    className="flex-1 inline-flex items-center justify-center gap-1 rounded-full px-3 py-1.5 text-sm border border-border hover:bg-muted"
                                >
                                    <Pencil className="h-3.5 w-3.5" /> Modifier
                                </button>
                                <button
                                    onClick={() => remove(s)}
                                    className="inline-flex items-center gap-1 rounded-full px-3 py-1.5 text-sm border border-border text-destructive hover:bg-destructive/10"
                                >
                                    <Trash2 className="h-3.5 w-3.5" />
                                </button>
                            </div>
                        </li>
                    ))}
                </ul>
            )}

            {editing && (
                <SupplierEditor
                    initial={editing === "new" ? EMPTY : editing}
                    isNew={editing === "new"}
                    onClose={() => setEditing(null)}
                    onSaved={() => { setEditing(null); load(); }}
                />
            )}
        </div>
    );
}


function Tag({ children, color = "default" }) {
    const cls = {
        default: "bg-primary/10 text-primary",
        green: "bg-green-100 text-green-800",
        neutral: "bg-muted text-foreground/70",
    }[color];
    return <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${cls}`}>{children}</span>;
}


// ---------------------------------------------------------------------------
// Modal d'édition
// ---------------------------------------------------------------------------
function SupplierEditor({ initial, isNew, onClose, onSaved }) {
    const [form, setForm] = useState({ ...EMPTY, ...initial });
    const [saving, setSaving] = useState(false);
    const [uploading, setUploading] = useState(false);
    const fileRef = useRef(null);
    const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

    function setList(k, raw) {
        // Liste séparée par ligne dans le textarea → array
        const arr = raw.split("\n").map((s) => s.trim()).filter(Boolean);
        set(k, arr);
    }

    async function save() {
        if (!form.slug || !form.name) {
            toast.error("Slug et nom requis");
            return;
        }
        setSaving(true);
        try {
            const payload = {
                ...form,
                // Normalise les ratings en number
                transparency_rating: Number(form.transparency_rating),
                customer_service_rating: Number(form.customer_service_rating),
                green_rating: Number(form.green_rating),
                price_rating: Number(form.price_rating),
                digital_rating: Number(form.digital_rating),
                overall_rating: Number(form.overall_rating),
                price_tier: Number(form.price_tier),
                year_founded: form.year_founded ? Number(form.year_founded) : null,
            };
            if (isNew) {
                await api.post("/admin/suppliers", payload);
                toast.success("Fournisseur créé");
            } else {
                await api.put(`/admin/suppliers/${initial.slug}`, payload);
                toast.success("Fournisseur mis à jour");
            }
            onSaved && onSaved();
        } catch (e) {
            toast.error("Sauvegarde : " + _extractError(e));
        } finally {
            setSaving(false);
        }
    }

    async function uploadLogo(file) {
        if (!file) return;
        if (isNew) {
            toast.error("Enregistre d'abord le fournisseur, puis upload le logo");
            return;
        }
        setUploading(true);
        try {
            const fd = new FormData();
            fd.append("file", file);
            const { data } = await api.post(
                `/admin/suppliers/${initial.slug}/logo`,
                fd,
                { headers: { "Content-Type": "multipart/form-data" } },
            );
            set("logo", data.logo);
            toast.success("Logo mis à jour");
        } catch (e) {
            toast.error("Upload : " + _extractError(e));
        } finally {
            setUploading(false);
        }
    }

    return (
        <div className="fixed inset-0 z-50 bg-foreground/50 backdrop-blur-sm flex items-start justify-center p-4 overflow-y-auto">
            <div className="bg-background w-full max-w-4xl rounded-3xl shadow-2xl my-8 overflow-hidden">
                <header className="bg-primary text-primary-foreground p-5 flex items-center justify-between">
                    <div>
                        <p className="text-xs uppercase tracking-[0.18em] opacity-80">
                            {isNew ? "Nouveau fournisseur" : "Modifier fournisseur"}
                        </p>
                        <h2 className="font-display text-2xl font-bold mt-1">{form.name || "Sans nom"}</h2>
                    </div>
                    <button onClick={onClose} className="p-2 rounded-full hover:bg-white/10">
                        <X className="h-5 w-5" />
                    </button>
                </header>

                <div className="p-6 space-y-6 max-h-[70vh] overflow-y-auto">
                    {/* Identité */}
                    <section>
                        <h3 className="font-display text-lg font-bold text-primary mb-3">Identité</h3>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                            <F label="Nom" value={form.name} onChange={(v) => set("name", v)} />
                            <F label="Slug (url)" value={form.slug} onChange={(v) => set("slug", v.toLowerCase().replace(/[^a-z0-9-]/g, "-"))} disabled={!isNew} placeholder="edf, engie, …" />
                            <F label="Site web" value={form.website || ""} onChange={(v) => set("website", v)} placeholder="https://…" />
                            <F label="Année de création" type="number" value={form.year_founded || ""} onChange={(v) => set("year_founded", v)} />
                            <F label="Siège" value={form.headquarters || ""} onChange={(v) => set("headquarters", v)} placeholder="Paris, France" />
                            <Select label="Audience" value={form.target_audience} onChange={(v) => set("target_audience", v)}
                                options={[{value:"b2c",label:"Particuliers"},{value:"b2b",label:"Entreprises"},{value:"both",label:"Les deux"}]} />
                            <F label="Accroche (tagline)" value={form.tagline} onChange={(v) => set("tagline", v)} colSpan />
                            <TextArea label="Description" value={form.description} onChange={(v) => set("description", v)} rows={3} colSpan />
                        </div>
                    </section>

                    {/* Logo */}
                    <section>
                        <h3 className="font-display text-lg font-bold text-primary mb-3">Logo</h3>
                        <div className="flex items-center gap-4">
                            <div className="h-20 w-20 rounded-xl bg-muted overflow-hidden flex items-center justify-center">
                                {form.logo ? (
                                    <img src={assetUrl(form.logo)} alt="logo" className="h-full w-full object-contain" />
                                ) : (
                                    <ImageIcon className="h-7 w-7 text-muted-foreground" />
                                )}
                            </div>
                            <div>
                                <button
                                    onClick={() => fileRef.current?.click()}
                                    disabled={uploading || isNew}
                                    className="inline-flex items-center gap-2 rounded-full px-4 py-2 text-sm border-2 border-border bg-card hover:border-primary/40 disabled:opacity-50"
                                >
                                    {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
                                    Téléverser un logo
                                </button>
                                {isNew && <p className="text-xs text-muted-foreground mt-1">Enregistre d'abord pour activer l'upload.</p>}
                                <input
                                    ref={fileRef}
                                    type="file"
                                    accept="image/png,image/jpeg,image/webp"
                                    className="hidden"
                                    onChange={(e) => uploadLogo(e.target.files?.[0])}
                                />
                            </div>
                        </div>
                    </section>

                    {/* Services & énergies */}
                    <section>
                        <h3 className="font-display text-lg font-bold text-primary mb-3">Énergies & Services</h3>
                        <div className="grid grid-cols-3 gap-3 mb-4">
                            <Toggle label="Électricité" checked={form.electricity} onChange={(v) => set("electricity", v)} />
                            <Toggle label="Gaz" checked={form.gas} onChange={(v) => set("gas", v)} />
                            <Toggle label="Vert" checked={form.green_energy} onChange={(v) => set("green_energy", v)} />
                        </div>
                        <TextArea
                            label="Services (un par ligne)"
                            value={(form.services || []).join("\n")}
                            onChange={(v) => setList("services", v)}
                            rows={4}
                        />
                    </section>

                    {/* Pros / Cons */}
                    <section>
                        <h3 className="font-display text-lg font-bold text-primary mb-3">Atouts & Limites</h3>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                            <TextArea label="Atouts (un par ligne)" value={(form.pros || []).join("\n")} onChange={(v) => setList("pros", v)} rows={4} />
                            <TextArea label="Limites (un par ligne)" value={(form.cons || []).join("\n")} onChange={(v) => setList("cons", v)} rows={4} />
                        </div>
                        <F label="Idéal pour…" value={form.best_for || ""} onChange={(v) => set("best_for", v)} placeholder="Foyers attachés à la stabilité…" colSpan />
                    </section>

                    {/* Notes */}
                    <section>
                        <h3 className="font-display text-lg font-bold text-primary mb-3">Notes (0–5)</h3>
                        <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                            <Rating label="Note globale" value={form.overall_rating} onChange={(v) => set("overall_rating", v)} />
                            <Rating label="Transparence" value={form.transparency_rating} onChange={(v) => set("transparency_rating", v)} />
                            <Rating label="Service client" value={form.customer_service_rating} onChange={(v) => set("customer_service_rating", v)} />
                            <Rating label="Énergie verte" value={form.green_rating} onChange={(v) => set("green_rating", v)} />
                            <Rating label="Prix" value={form.price_rating} onChange={(v) => set("price_rating", v)} />
                            <Rating label="Digital" value={form.digital_rating} onChange={(v) => set("digital_rating", v)} />
                            <Select
                                label="Tier prix (1=cher, 5=économique)"
                                value={String(form.price_tier)}
                                onChange={(v) => set("price_tier", Number(v))}
                                options={[1,2,3,4,5].map((n) => ({value:String(n), label:String(n)}))}
                            />
                        </div>
                    </section>
                </div>

                <footer className="border-t border-border p-4 flex items-center justify-end gap-2">
                    <button onClick={onClose} className="rounded-full px-4 py-2 text-sm border border-border hover:bg-muted">
                        Annuler
                    </button>
                    <button
                        onClick={save}
                        disabled={saving}
                        className="inline-flex items-center gap-2 rounded-full px-6 py-2 text-sm font-semibold bg-primary text-primary-foreground hover:opacity-90 disabled:opacity-50"
                    >
                        {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
                        Enregistrer
                    </button>
                </footer>
            </div>
        </div>
    );
}


function F({ label, value, onChange, type = "text", placeholder, disabled, colSpan }) {
    return (
        <label className={`block ${colSpan ? "md:col-span-2" : ""}`}>
            <span className="text-xs font-semibold text-muted-foreground">{label}</span>
            <input
                type={type}
                value={value ?? ""}
                onChange={(e) => onChange(e.target.value)}
                placeholder={placeholder}
                disabled={disabled}
                className="mt-1 w-full rounded-lg border border-border bg-background px-3 py-2 text-sm disabled:opacity-50"
            />
        </label>
    );
}

function TextArea({ label, value, onChange, rows = 3, colSpan }) {
    return (
        <label className={`block ${colSpan ? "md:col-span-2" : ""}`}>
            <span className="text-xs font-semibold text-muted-foreground">{label}</span>
            <textarea
                value={value ?? ""}
                onChange={(e) => onChange(e.target.value)}
                rows={rows}
                className="mt-1 w-full rounded-lg border border-border bg-background px-3 py-2 text-sm resize-y"
            />
        </label>
    );
}

function Select({ label, value, onChange, options }) {
    return (
        <label className="block">
            <span className="text-xs font-semibold text-muted-foreground">{label}</span>
            <select value={value} onChange={(e) => onChange(e.target.value)}
                className="mt-1 w-full rounded-lg border border-border bg-background px-3 py-2 text-sm">
                {options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
        </label>
    );
}

function Toggle({ label, checked, onChange }) {
    return (
        <button
            type="button"
            onClick={() => onChange(!checked)}
            className={`flex items-center justify-between gap-2 rounded-lg border px-3 py-2 text-sm font-medium transition-colors ${
                checked ? "bg-primary text-primary-foreground border-primary" : "bg-card text-foreground/80 border-border hover:border-primary/40"
            }`}
        >
            {label}
            <span className={`inline-block h-4 w-4 rounded-full ${checked ? "bg-primary-foreground" : "bg-muted"}`} />
        </button>
    );
}

function Rating({ label, value, onChange }) {
    const v = Number(value) || 0;
    return (
        <div>
            <span className="text-xs font-semibold text-muted-foreground">{label}</span>
            <div className="mt-1 flex items-center gap-2">
                <input
                    type="number"
                    min={0}
                    max={5}
                    step={0.1}
                    value={v}
                    onChange={(e) => onChange(e.target.value)}
                    className="w-20 rounded-lg border border-border bg-background px-2 py-1 text-sm"
                />
                <div className="flex">
                    {[1,2,3,4,5].map((i) => (
                        <Star key={i} className={`h-3.5 w-3.5 ${i <= Math.round(v) ? "fill-accent-foreground text-accent-foreground" : "text-muted-foreground/40"}`} />
                    ))}
                </div>
            </div>
        </div>
    );
}
